import { useSyncExternalStore } from 'react';
import type { OutgoingAttachment } from '../../api/comms';
import type { ConversationDraft, ConversationDraftSummary } from '../../api/types';

/**
 * The reply an agent started and has not sent.
 *
 * The record of truth is the SERVER (`message_drafts`, one row per person per
 * conversation): a draft written at the desk is there on the laptop, and the
 * Drafts view can list it. This module is the two things a server cannot be —
 *
 *  1. a local buffer, because the save is debounced and a closed tab, a reload
 *     or a dropped connection must not take the sentence with it. Whichever
 *     copy is NEWER wins when the composer restores, so a save that never left
 *     the browser is still the one that comes back;
 *  2. a store of what the LIST knows, so the "Draft" line on a row and the
 *     count beside the view update as the agent types rather than on the next
 *     list read.
 *
 * The local buffer is keyed by tenant AND by the agent's address: `display_id`
 * is a per-tenant counter, so conversation 42 exists in every tenant, and a
 * dispatch office shares browser profiles — the next person to sign in here
 * must not find someone else's unsent words waiting in a box.
 */

/** The composer's own shape, matching the wire body of PUT .../draft. */
export interface DraftBody {
  content: string;
  bodyHtml: string | null;
  isNote: boolean;
  attachments: OutgoingAttachment[];
  /**
   * Who the reply is addressed to, on a channel that can address more than one
   * person. Part of the draft rather than beside it: on an email thread the
   * recipient list is half of what was written, and a buffer that restores the
   * body alone hands the agent a complete-looking reply going to one person.
   */
  toEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
  /**
   * A forward's own title, and the flag that says this draft IS a forward — a
   * half-written one restored without it comes back addressed to the people it
   * was going to and titled as a reply to the thread it was leaving.
   */
  subject: string | null;
}

export interface BufferedDraft extends DraftBody {
  updatedAt: string;
}

const PREFIX = 'crm.composerDraft.';
/** Newest kept per browser. Beyond this the server is the copy that matters. */
const MAX_DRAFTS = 40;
/** A fortnight-old buffer is a stale thought; the server row outlives it. */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/** One buffer slot: the scope from [CommsState.draftScope], and the thread. */
export function composerDraftKey(scope: string, conversationId: number): string {
  return `${scope}:${conversationId}`;
}

function isAttachment(value: unknown): value is OutgoingAttachment {
  if (typeof value !== 'object' || value === null) return false;
  const { s3Key, fileName, fileType, fileSize, extension } = value as Record<string, unknown>;
  return (
    typeof s3Key === 'string' &&
    typeof fileName === 'string' &&
    typeof fileSize === 'number' &&
    typeof extension === 'string' &&
    (fileType === 'image' || fileType === 'audio' || fileType === 'video' || fileType === 'file')
  );
}

/**
 * Blank is not a draft — it is the composer as everyone else finds it. Answered
 * from the text and the attachments rather than the markup, because an empty
 * styled box still reports leftovers like `<br>` as its html.
 */
export function isBlankDraft(draft: DraftBody): boolean {
  return !draft.content.trim() && draft.attachments.length === 0;
}

/**
 * The three recipient lists off a stored row. Rows written before this shipped
 * carry none of them, so each falls back to empty rather than to undefined —
 * the composer spreads these straight into state.
 */
function readRecipients(
  row: Record<string, unknown>,
): Pick<DraftBody, 'toEmails' | 'ccEmails' | 'bccEmails' | 'subject'> {
  const list = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
  return {
    toEmails: list(row.toEmails),
    ccEmails: list(row.ccEmails),
    bccEmails: list(row.bccEmails),
    subject: typeof row.subject === 'string' && row.subject ? row.subject : null,
  };
}

function parseDraft(raw: string | null): BufferedDraft | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const row = parsed as Record<string, unknown>;
    const draft: BufferedDraft = {
      content: typeof row.content === 'string' ? row.content : '',
      bodyHtml: typeof row.bodyHtml === 'string' ? row.bodyHtml : null,
      isNote: row.isNote === true,
      attachments: Array.isArray(row.attachments) ? row.attachments.filter(isAttachment) : [],
      ...readRecipients(row),
      updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : '',
    };
    return isBlankDraft(draft) ? null : draft;
  } catch {
    return null;
  }
}

/**
 * Which stored rows to drop: everything past the age horizon, then the oldest
 * of what is left once there are more than a desk's worth. Pure so the policy
 * is testable without a browser.
 */
export function draftsToEvict(entries: { key: string; at: number }[], now: number): string[] {
  const evict: string[] = [];
  const live: { key: string; at: number }[] = [];
  for (const entry of entries) {
    if (now - entry.at > MAX_AGE_MS) evict.push(entry.key);
    else live.push(entry);
  }
  live.sort((a, b) => b.at - a.at);
  for (const entry of live.slice(MAX_DRAFTS)) evict.push(entry.key);
  return evict;
}

/** Drop what nobody is coming back to. Only run when a slot is new. */
export function pruneComposerDrafts(now = Date.now()): void {
  try {
    const entries: { key: string; at: number }[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(PREFIX)) continue;
      // An unreadable row can only be a shape this build no longer writes, so
      // it evicts with the oldest rather than sitting there forever.
      const at = Date.parse(parseDraft(localStorage.getItem(key))?.updatedAt ?? '');
      entries.push({ key, at: Number.isFinite(at) ? at : 0 });
    }
    for (const key of draftsToEvict(entries, now)) localStorage.removeItem(key);
  } catch {
    // Storage refused; there is nothing to prune and nothing to report.
  }
}

/** What this browser buffered for that composer, or null. */
export function readComposerDraft(key: string, now = Date.now()): BufferedDraft | null {
  try {
    const draft = parseDraft(localStorage.getItem(PREFIX + key));
    if (!draft) return null;
    const at = Date.parse(draft.updatedAt);
    // Age is re-checked here as well as in the prune: a browser that has not
    // written a draft in a month never ran one.
    return Number.isFinite(at) && now - at <= MAX_AGE_MS ? draft : null;
  } catch {
    return null;
  }
}

/**
 * Buffer it, now — before the debounced save, and instead of it when the
 * network is gone. A blank composer REMOVES the row, which is also how a sent
 * message forgets its own draft: the box empties and this follows.
 */
export function saveComposerDraft(key: string, draft: DraftBody | null, now = Date.now()): void {
  const storageKey = PREFIX + key;
  try {
    if (!draft || isBlankDraft(draft)) {
      localStorage.removeItem(storageKey);
      return;
    }
    const fresh = localStorage.getItem(storageKey) === null;
    const row = JSON.stringify({ ...draft, updatedAt: new Date(now).toISOString() });
    try {
      localStorage.setItem(storageKey, row);
    } catch {
      // Out of room. The drafts nobody is typing into are the ones to lose, so
      // prune and try once more before giving up on this one.
      pruneComposerDrafts(now);
      localStorage.setItem(storageKey, row);
    }
    if (fresh) pruneComposerDrafts(now);
  } catch {
    // A browser that refuses storage still gets a working composer, and the
    // server still has the draft; it just loses the unsaved tail.
  }
}

/**
 * Which of the two copies to restore. The server is authoritative about what it
 * holds, but the buffer is authoritative about what was typed after the last
 * save that got through — so the newer timestamp wins, and a missing one loses.
 */
export function newerDraft(
  server: ConversationDraft | null,
  buffered: BufferedDraft | null,
): (ConversationDraft | BufferedDraft) | null {
  if (!server) return buffered;
  if (!buffered) return server;
  return Date.parse(buffered.updatedAt) > Date.parse(server.updatedAt) ? buffered : server;
}

// ---------------------------------------------------------------------------
// What the list knows: one summary per conversation, live as the agent types.
// ---------------------------------------------------------------------------

/**
 * `null` means the server says there is no draft here; a missing entry means
 * nobody has said. Seeded from every list read — which always carries the key,
 * so a stale entry left by another tenant's conversation of the same number is
 * corrected by the first list that mentions it.
 */
let summaries: ReadonlyMap<number, ConversationDraftSummary | null> = new Map();
const listeners = new Set<() => void>();

function publish(next: ReadonlyMap<number, ConversationDraftSummary | null>) {
  summaries = next;
  for (const listener of listeners) listener();
}

/** Take what a list response said about drafts. Rows without the key are left alone. */
export function seedDraftSummaries(rows: { id: number; draft?: ConversationDraftSummary | null }[]): void {
  let changed = false;
  const next = new Map(summaries);
  for (const row of rows) {
    if (row.draft === undefined) continue;
    const current = next.get(row.id);
    if (current === undefined || JSON.stringify(current) !== JSON.stringify(row.draft)) {
      next.set(row.id, row.draft);
      changed = true;
    }
  }
  if (changed) publish(next);
}

/** The composer, as it types: what this row should now say. */
export function setDraftSummary(displayId: number, draft: DraftBody | null): void {
  const next = new Map(summaries);
  next.set(
    displayId,
    !draft || isBlankDraft(draft)
      ? null
      : {
          preview: draft.content.replace(/\s+/g, ' ').trim().slice(0, 140),
          isNote: draft.isNote,
          attachmentCount: draft.attachments.length,
          updatedAt: new Date().toISOString(),
        },
  );
  publish(next);
}

/**
 * What the store knows about one conversation: a summary, `null` for "the
 * server says there is none", or undefined for "nobody has said". The three
 * are different answers and the live list needs all of them — dropping a row
 * from the Drafts view on `undefined` would drop it every time an unrelated
 * socket update arrived, since those payloads carry no draft at all.
 */
export function draftSummaryOf(displayId: number): ConversationDraftSummary | null | undefined {
  return summaries.get(displayId);
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = () => summaries;

/** Every known draft summary, by conversation display id. */
export function useDraftSummaries(): ReadonlyMap<number, ConversationDraftSummary | null> {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
